/**
 * Toast notification system with React context.
 *
 * Setup in App.tsx:
 *   <ToastProvider>
 *     <App />
 *   </ToastProvider>
 *
 * Usage in components:
 *   const { toast } = useToast()
 *   toast.success('Analysis complete!')
 *   toast.error('Something went wrong')
 *   toast.warning('Low credits remaining')
 *   toast.info('Analysis started')
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react'

// Duration constants for toast notifications
const TOAST_DURATIONS = {
  default: 5000,
  error: 8000,
  persistent: 0,
} as const

// Types
type ToastType = 'success' | 'error' | 'warning' | 'info'

interface ToastItem {
  id: string
  type: ToastType
  message: string
  duration?: number
}

interface ToastContextValue {
  toast: {
    success: (message: string, duration?: number) => void
    error: (message: string, duration?: number) => void
    warning: (message: string, duration?: number) => void
    info: (message: string, duration?: number) => void
  }
  dismiss: (id: string) => void
}

// Context
const ToastContext = createContext<ToastContextValue | null>(null)

// Hook
export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}

// Styling config
const toastStyles: Record<ToastType, { bg: string; border: string; icon: ReactNode }> = {
  success: {
    bg: 'bg-success-50',
    border: 'border-success-200',
    icon: <CheckCircle className="w-5 h-5 text-success-600" />,
  },
  error: {
    bg: 'bg-danger-50',
    border: 'border-danger-200',
    icon: <XCircle className="w-5 h-5 text-danger-600" />,
  },
  warning: {
    bg: 'bg-warning-50',
    border: 'border-warning-200',
    icon: <AlertTriangle className="w-5 h-5 text-warning-600" />,
  },
  info: {
    bg: 'bg-primary-50',
    border: 'border-primary-200',
    icon: <Info className="w-5 h-5 text-primary-600" />,
  },
}

// Single toast component
function ToastNotification({
  item,
  onDismiss,
}: {
  item: ToastItem
  onDismiss: (id: string) => void
}) {
  const style = toastStyles[item.type]

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg ${style.bg} ${style.border} animate-slide-in`}
      role="alert"
    >
      <span className="flex-shrink-0 mt-0.5">{style.icon}</span>
      <p className="flex-1 text-sm font-medium text-gray-800">{item.message}</p>
      <button
        onClick={() => onDismiss(item.id)}
        className="flex-shrink-0 p-1 rounded-lg hover:bg-gray-200/50 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4 text-gray-500" />
      </button>
    </div>
  )
}

// Provider component
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const addToast = useCallback((type: ToastType, message: string, duration: number = TOAST_DURATIONS.default) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`
    const newToast: ToastItem = { id, type, message, duration }

    setToasts((prev) => [...prev, newToast])

    // Auto dismiss
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
      }, duration)
    }
  }, [])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = {
    success: (message: string, duration?: number) => addToast('success', message, duration),
    error: (message: string, duration?: number) => addToast('error', message, duration ?? TOAST_DURATIONS.error),
    warning: (message: string, duration?: number) => addToast('warning', message, duration),
    info: (message: string, duration?: number) => addToast('info', message, duration),
  }

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      {/* Toast container - fixed position */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
        {toasts.map((item) => (
          <ToastNotification key={item.id} item={item} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}
